// Phase RV-6b — FLEX History hook.
//
// Source: ROLLOVER Intelligence Enhancement v1.0 §11.3.1.
//
// Auth-only. Reads the caller's own rollover_flex_slips rows where
// status IN ('locked','settled'), ordered by created_at DESC.
//
// Realtime channel name uses useId() per-mount (mandatory pattern,
// mirroring usePendingDrift / useFlexBuilder / useRolloverNotifications).
//
// N15: this hook never reads or writes the chain table.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type FlexSlipStatus = "locked" | "settled";
export type FlexSlipOutcome = "won" | "lost" | "void" | "partial" | null;

export interface FlexSlipLeg {
  candidate_id: string | null;
  fixture_id: string | null;
  match_name: string | null;
  tier: "2x" | "3x" | "4x" | "5x" | "6x" | null;
  market: string | null;
  pick: string | null;
  odds: number | null;
  confidence_score: number | null;
  leg_outcome: "won" | "lost" | "void" | null;
}

export interface FlexSlip {
  flex_slip_id: string;
  window_label: "MIDWEEK" | "WEEKEND" | "SUPPLEMENT" | null;
  window_publication_at: string | null;
  legs: FlexSlipLeg[];
  leg_count: number;
  combined_odds: number;
  stake_amount: number | null;
  potential_return: number | null;
  status: FlexSlipStatus;
  outcome: FlexSlipOutcome;
  locked_at: string | null;
  settled_at: string | null;
  created_at: string;
}

export interface UseFlexHistoryState {
  slips: FlexSlip[];
  settledCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function normaliseLegs(raw: unknown): FlexSlipLeg[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    const row = (r ?? {}) as Record<string, unknown>;
    const tier = row.tier;
    const validTier =
      tier === "2x" || tier === "3x" || tier === "4x" || tier === "5x" || tier === "6x"
        ? tier
        : null;
    const lo = row.leg_outcome;
    const validLo =
      lo === "won" || lo === "lost" || lo === "void" ? lo : null;
    return {
      candidate_id: typeof row.candidate_id === "string" ? row.candidate_id : null,
      fixture_id: typeof row.fixture_id === "string" ? row.fixture_id : null,
      match_name: typeof row.match_name === "string" ? row.match_name : null,
      tier: validTier,
      market: typeof row.market === "string" ? row.market : null,
      pick: typeof row.pick === "string" ? row.pick : null,
      odds:
        typeof row.odds === "number"
          ? row.odds
          : row.odds !== null && row.odds !== undefined
          ? Number(row.odds)
          : null,
      confidence_score:
        typeof row.confidence_score === "number"
          ? row.confidence_score
          : row.confidence_score !== null && row.confidence_score !== undefined
          ? Number(row.confidence_score)
          : null,
      leg_outcome: validLo,
    };
  });
}

function normaliseSlip(row: Record<string, unknown>): FlexSlip | null {
  if (typeof row.flex_slip_id !== "string") return null;
  const status = row.status;
  if (status !== "locked" && status !== "settled") return null;
  const wl = row.window_label;
  const validWl =
    wl === "MIDWEEK" || wl === "WEEKEND" || wl === "SUPPLEMENT" ? wl : null;
  const oc = row.outcome;
  const validOc =
    oc === "won" || oc === "lost" || oc === "void" || oc === "partial"
      ? oc
      : null;
  const legs = normaliseLegs(row.legs);
  return {
    flex_slip_id: row.flex_slip_id,
    window_label: validWl,
    window_publication_at:
      typeof row.window_publication_at === "string"
        ? row.window_publication_at
        : null,
    legs,
    leg_count:
      typeof row.leg_count === "number" ? row.leg_count : legs.length,
    combined_odds:
      typeof row.combined_odds === "number"
        ? row.combined_odds
        : row.combined_odds !== null && row.combined_odds !== undefined
        ? Number(row.combined_odds)
        : 0,
    stake_amount:
      typeof row.stake_amount === "number"
        ? row.stake_amount
        : row.stake_amount !== null && row.stake_amount !== undefined
        ? Number(row.stake_amount)
        : null,
    potential_return:
      typeof row.potential_return === "number"
        ? row.potential_return
        : row.potential_return !== null && row.potential_return !== undefined
        ? Number(row.potential_return)
        : null,
    status,
    outcome: validOc,
    locked_at: typeof row.locked_at === "string" ? row.locked_at : null,
    settled_at: typeof row.settled_at === "string" ? row.settled_at : null,
    created_at:
      typeof row.created_at === "string"
        ? row.created_at
        : new Date().toISOString(),
  };
}

export function useFlexHistory(): UseFlexHistoryState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const instanceId = useId();
  const [slips, setSlips] = useState<FlexSlip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!userId) {
      setSlips([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("rollover_flex_slips")
      .select(
        "flex_slip_id, window_label, window_publication_at, legs, leg_count, combined_odds, stake_amount, potential_return, status, outcome, locked_at, settled_at, created_at",
      )
      .eq("user_id", userId)
      .in("status", ["locked", "settled"])
      .order("created_at", { ascending: false });
    if (res.error) {
      setError(res.error.message ?? "Failed to load FLEX history");
      setSlips([]);
      setLoading(false);
      return;
    }
    const list = Array.isArray(res.data) ? res.data : [];
    const out: FlexSlip[] = [];
    for (const row of list) {
      if (row && typeof row === "object") {
        const s = normaliseSlip(row as Record<string, unknown>);
        if (s) out.push(s);
      }
    }
    setSlips(out);
    setLoading(false);
  }, [supabase, userId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Realtime — own-uid filter, per-mount channel id from useId().
  useEffect(() => {
    if (!userId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const channel: RealtimeChannel = sb
      .channel(`rollover_flex_slips:${userId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "rollover_flex_slips",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, userId, refetch, instanceId]);

  const settledCount = useMemo(
    () => slips.filter((s) => s.status === "settled").length,
    [slips],
  );

  return useMemo<UseFlexHistoryState>(
    () => ({ slips, settledCount, loading, error, refetch }),
    [slips, settledCount, loading, error, refetch],
  );
}

/**
 * Pure helper — derives the badge identity for a slip's (status, outcome).
 * Mirrors §11.3.1 outcome badge spec.
 */
export type FlexOutcomeBadge =
  | { kind: "pending" }
  | { kind: "won" }
  | { kind: "lost" }
  | { kind: "void" }
  | { kind: "partial" };

export function flexOutcomeBadge(
  status: FlexSlipStatus,
  outcome: FlexSlipOutcome,
): FlexOutcomeBadge {
  if (status === "locked" || outcome === null) return { kind: "pending" };
  if (outcome === "won") return { kind: "won" };
  if (outcome === "lost") return { kind: "lost" };
  if (outcome === "void") return { kind: "void" };
  if (outcome === "partial") return { kind: "partial" };
  return { kind: "pending" };
}
